/**
 * Caps how much of the transcript a single Mermaid diagram can occupy, wired in
 * as the `pre` override on `RESPONSE_MARKDOWN_COMPONENTS`.
 *
 * Streamdown renders the diagram SVG at its natural size, so a tall
 * composite-state or gantt chart produces an equally tall card — fine for the
 * one-diagram answer, punishing when Coco replies with three or four and the
 * prose between them scrolls off. Anything past
 * {@link COLLAPSED_MAX_HEIGHT_PX} is clipped with a fade and an "Expand
 * <label>" button; shorter diagrams are left exactly as they were, so the
 * common case pays nothing.
 *
 * ## Why `pre` and not `code`
 *
 * The mermaid block is built by Streamdown's own `code` component — it owns the
 * lazy `Mermaid` import, the download/copy/fullscreen row and the pan/zoom
 * shell, and none of those are on its public export surface, so overriding
 * `code` would mean reimplementing all of it. Streamdown maps `pre` to a bare
 * passthrough (`({ children }) => children`), which makes it a free wrapper
 * slot around the finished block. Non-mermaid fences keep that passthrough
 * behavior exactly.
 *
 * One deliberate side effect of clipping: Streamdown anchors its pan/zoom pill
 * `absolute bottom-2 left-2` inside the diagram, so it scrolls out of view
 * while collapsed and returns on expand. That's the right order of operations —
 * expanding is the affordance a clipped diagram should offer, not zooming.
 */

import {
  isValidElement,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { mermaidDiagramLabel } from './mermaid-diagram-label';

/**
 * How tall a diagram may be before it is clipped. Roughly half a laptop
 * viewport: enough that most flowcharts render whole, small enough that four
 * diagrams still fit in one scroll.
 */
export const COLLAPSED_MAX_HEIGHT_PX = 360;

/**
 * The Mermaid source behind a `<pre>`, or `undefined` when this fence isn't a
 * diagram.
 *
 * `children` here is Streamdown's rendered `code` element, whose `className`
 * carries the `language-mermaid` marker its own renderer keys off. The source
 * text sits one level down and arrives either as a bare string or wrapped in an
 * element, depending on which rehype plugins touched the node — both shapes are
 * read, and an unreadable one still returns `''` so the block is treated as a
 * diagram (with a generic label) rather than falling through to the
 * passthrough.
 */
function mermaidSource(children: ReactNode): string | undefined {
  if (!isValidElement<{ className?: string; children?: ReactNode }>(children)) {
    return undefined;
  }

  const { className, children: inner } = children.props;
  if (className === undefined || !className.includes('language-mermaid')) {
    return undefined;
  }

  if (typeof inner === 'string') {
    return inner;
  }
  if (
    isValidElement<{ children?: ReactNode }>(inner) &&
    typeof inner.props.children === 'string'
  ) {
    return inner.props.children;
  }
  return '';
}

function MermaidCollapsible({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = contentRef.current;
    if (element === null) {
      return undefined;
    }

    // Measured on the inner element, which is never itself clipped — the
    // `max-h` lives on its parent. Observing the clipped box instead would go
    // quiet the moment it hit the cap, and Mermaid renders asynchronously (and
    // re-renders as the fence streams), so the size that matters arrives well
    // after mount.
    const measure = (): void => {
      setOverflows(element.offsetHeight > COLLAPSED_MAX_HEIGHT_PX);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const collapsed = overflows && !expanded;

  return (
    <div className="relative" data-streamdown="mermaid-collapsible">
      <div
        className={cn(collapsed && 'overflow-hidden')}
        style={
          collapsed ? { maxHeight: `${COLLAPSED_MAX_HEIGHT_PX}px` } : undefined
        }
      >
        <div ref={contentRef}>{children}</div>
      </div>

      {collapsed && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-px bottom-px h-28 rounded-b-xl bg-gradient-to-t from-background via-background/85 to-transparent"
        />
      )}

      {overflows && (
        <div
          className={cn(
            'flex justify-center',
            // Collapsed, the button floats over the fade it belongs to;
            // expanded, it sits below the diagram so it never covers content.
            collapsed ? 'absolute inset-x-0 bottom-4' : 'mt-1 mb-4'
          )}
        >
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            className="flex items-center gap-1 rounded-full border border-border bg-background/90 px-3 py-1 text-muted-foreground text-xs shadow-sm backdrop-blur-sm transition-colors hover:text-foreground"
          >
            {collapsed ? (
              <ChevronDownIcon className="size-3" />
            ) : (
              <ChevronUpIcon className="size-3" />
            )}
            {collapsed ? `Expand ${label}` : `Collapse ${label}`}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * `pre` override for `RESPONSE_MARKDOWN_COMPONENTS`. Wraps Mermaid blocks in
 * {@link MermaidCollapsible} and passes every other fence straight through,
 * matching Streamdown's own `pre`.
 */
export function ResponsePre({ children }: ComponentProps<'pre'>): JSX.Element {
  const source = mermaidSource(children);
  if (source === undefined) {
    return <>{children}</>;
  }
  return (
    <MermaidCollapsible label={mermaidDiagramLabel(source)}>
      {children}
    </MermaidCollapsible>
  );
}
